import type { ProviderAccount, ProviderAccountTestResult } from '@/api/schemas';
import { formatDateTime, humanize, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { RowActions } from '@/components/RowActions';
import { StatusPill, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { Card } from '@/components/ui/card';
import { circuitLook, managedByLook, menuActions, slotsLook } from './providerAccountModel';
import { TestResultChecks } from './TestResultChecks';
import type { ProviderAccountActions } from './useProviderAccountActions';

function ManagedByTag({ a }: { a: ProviderAccount }) {
  const look = managedByLook(a);
  return (
    <Tag title={look.title} className="shrink-0">
      {look.label}
    </Tag>
  );
}

function CardHeading({ a }: { a: ProviderAccount }) {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg border bg-card font-mono text-[11px] font-semibold', a.kind === 'bitrise' ? 'text-bitrise' : 'text-foreground')}>{a.kind === 'bitrise' ? 'BIT' : 'EAS'}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-[15px] font-semibold text-foreground">{a.name}</span>
          <StatusPill status={a.status} label={humanize(a.status)} />
          <ManagedByTag a={a} />
        </div>
        <div className="truncate font-mono text-xs text-subtle">
          {a.externalAccount ?? '—'}
          {a.plan ? ` · ${a.plan}` : ''}
        </div>
      </div>
      <ProviderBadge kind={a.kind} />
    </div>
  );
}

function HealthValue({ health, now }: { health: ProviderAccount['health']; now: number }) {
  if (!health?.checkedAt) return <span className="text-subtle">never checked</span>;
  const look = circuitLook(health.circuit);
  return (
    <span className={look.tone} title={formatDateTime(health.checkedAt)}>
      {look.label} · {health.latencyMs ?? '—'} ms · {timeAgo(health.checkedAt, now)}
    </span>
  );
}

function LastError({ message }: { message: string }) {
  return (
    <>
      <dt className="text-subtle">Last error</dt>
      <dd className="truncate font-mono text-failed" title={message}>
        {message}
      </dd>
    </>
  );
}

function CliVersion({ version }: { version: string }) {
  return (
    <>
      <dt className="text-subtle">CLI</dt>
      <dd className="font-mono text-muted-foreground">{version}</dd>
    </>
  );
}

function CardFacts({ a, now }: { a: ProviderAccount; now: number }) {
  const slots = slotsLook(a);
  const cliVersion = a.settings.cliVersion;
  return (
    // Phones: one column (label above value) so a long health line or error never overflows the card.
    <dl className="grid min-w-0 grid-cols-1 gap-x-3 gap-y-1 text-xs sm:grid-cols-[auto_minmax(0,1fr)]">
      <dt className="text-subtle">Slots</dt>
      <dd className="tabular font-mono">
        <span className={slots.tone}>
          {a.inUse}/{a.concurrencyLimit}
        </span>
        <span className="text-subtle"> {slots.note}</span>
      </dd>
      <dt className="text-subtle">Health</dt>
      <dd className="min-w-0 break-words font-mono">
        <HealthValue health={a.health} now={now} />
      </dd>
      {a.health?.lastError ? <LastError message={a.health.lastError} /> : null}
      {typeof cliVersion === 'string' ? <CliVersion version={cliVersion} /> : null}
    </dl>
  );
}

/** A client admin's footer: read-only, set up by Hangar's administrators (SPEC decisions 19 and 30). */
function ReadOnlyFooter({ a }: { a: ProviderAccount }) {
  return (
    <div className="mt-auto flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
      <Icon name="verified_user" size={16} className="text-subtle" />
      Provided by Hangar
      <Tag className="ml-auto">since {formatDateTime(a.createdAt)}</Tag>
    </div>
  );
}

function CardFooter({ a, actions }: { a: ProviderAccount; actions: ProviderAccountActions }) {
  const disabled = a.status === 'disabled';
  if (!actions.operator) return <ReadOnlyFooter a={a} />;
  return (
    <div className="mt-auto flex flex-wrap items-center gap-2 border-t pt-3">
      <GatedButton size="sm" variant="outline" data-testid="provider-account-test" can={actions.can} loading={actions.testing === a.id} disabled={disabled} onClick={() => void actions.runTest(a)}>
        <Icon name="bolt" size={16} />
        Test connection
      </GatedButton>
      {disabled ? (
        <GatedButton size="sm" variant="outline" data-testid="provider-account-enable" can={actions.can} onClick={() => void actions.setStatus(a, 'active')}>
          Enable
        </GatedButton>
      ) : null}
      <Tag className="ml-auto">since {formatDateTime(a.createdAt)}</Tag>
      <RowActions actions={menuActions(actions.actionsFor(a))} subject={a.name} variant="menu" testId={`provider-account-actions-${a.externalAccount ?? a.id}`} />
    </div>
  );
}

interface CardProps {
  account: ProviderAccount;
  index: number;
  now: number;
  actions: ProviderAccountActions;
  result: ProviderAccountTestResult | undefined;
}

/** One provider account: provider, status, slots, health, the last "Test connection" and its actions. */
export function ProviderAccountCard({ account: a, index, now, actions, result }: CardProps) {
  return (
    <li data-testid={`provider-account-${a.kind}-${a.externalAccount ?? a.id}`} data-status={a.status} style={stagger(index)} className="enter min-w-0">
      <Card className={cn('lift h-full min-w-0 gap-3 bg-secondary/40 p-4 shadow-none', a.status === 'disabled' && 'opacity-70')}>
        <CardHeading a={a} />
        <CardFacts a={a} now={now} />
        {result ? <TestResultChecks result={result} /> : null}
        <CardFooter a={a} actions={actions} />
      </Card>
    </li>
  );
}
